import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  updateDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { AuthData } from '../../provider/AuthProvider';
import { 
  FaClock, 
  FaCheck, 
  FaTimes, 
  FaCalendar, 
  FaEye,
  FaExclamationTriangle,
  FaBell,
  FaSpinner,
  FaEnvelope
} from 'react-icons/fa';
import { 
  APPLICATION_STATUSES,
  getApplicationWithTimeoutInfo,
  studentAcceptOffer,
  studentDeclineOffer,
  OfferResponseComponent
} from '../../components/timeoutSystem';
import { emailNotifications } from '../../components/notifications/EmailNotificationComponent';

const Applications = () => {
  const { user } = AuthData();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [filter, setFilter] = useState('all');

  // Fetch applications on component mount
  useEffect(() => {
    const fetchApplications = async () => {
      if (!user?.userd?.uid) return;

      try {
        setLoading(true);
        const applicationsRef = collection(db, 'applications');
        const applicationsQuery = query(
          applicationsRef,
          where('studentId', '==', user.userd.uid),
          orderBy('appliedAt', 'desc')
        );
        
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Add timeout info to applications
        const applicationsWithTimeout = applicationsData.map(app => 
          getApplicationWithTimeoutInfo(app)
        );

        setApplications(applicationsWithTimeout);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [user]);

  // Handle student response to offer
  const handleOfferResponse = async (action, application, reason = '') => {
    setResponding(true);
    try {
      if (action === 'accept') {
        await studentAcceptOffer(application.id, user.userd.uid);

        // Send email notification to employer
        try {
          const notificationData = {
            employerData: {
              email: application.employerEmail,
            },
            studentData: {
              name: application.studentName,
              email: application.studentEmail
            },
            positionData: {
              title: application.positionTitle
            }
          };
    
          await emailNotifications.notifyStudentResponse(
            notificationData.employerData,
            notificationData.studentData,
            notificationData.positionData,
            'accepted'
          );
          
          console.log('✅ Employer notification sent for student acceptance');
        } catch (emailError) {
          console.error('❌ Error sending email notification:', emailError);
          // Don't fail the operation if email fails
        }

      } else if (action === 'decline') {
        await studentDeclineOffer(application.id, user.userd.uid, reason);

        // Send email notification to employer
        try {
          const notificationData = {
            employerData: {
              email: application.employerEmail,
            },
            studentData: {
              name: application.studentName,
              email: application.studentEmail
            },
            positionData: {
              title: application.positionTitle
            }
          };

          await emailNotifications.notifyStudentResponse(
            notificationData.employerData,
            notificationData.studentData,
            notificationData.positionData,
            'declined',
            reason
          );
          
          console.log('✅ Employer notification sent for student decline');
        } catch (emailError) {
          console.error('❌ Error sending email notification:', emailError);
          // Don't fail the operation if email fails
        }

      }

      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === application.id 
            ? {
                ...app, 
                status: action === 'accept' ? APPLICATION_STATUSES.STUDENT_ACCEPTED : APPLICATION_STATUSES.STUDENT_DECLINED,
                timeoutEnabled: false,
                studentResponseAt: new Date().toISOString(),
                studentDecision: action,
                ...(reason && { declineReason: reason })
              }
            : app
        )
      );

      // Show success message
      if (action === 'accept') {
        alert('🎉 Congratulations! You have accepted the internship offer. The employer will be notified.');
      } else {
        alert('Your response has been recorded. The employer will be notified of your decision.');
      }

    } catch (error) {
      console.error('Error responding to offer:', error);
      alert('Error processing your response. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  // Handle withdraw application
  const handleWithdrawApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    setResponding(true);
    try {
      // Update application status to withdrawn
      await updateDoc(doc(db, 'applications', applicationId), {
        status: APPLICATION_STATUSES.WITHDRAWN,
        withdrawnAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? {
                ...app, 
                status: APPLICATION_STATUSES.WITHDRAWN,
                withdrawnAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }
            : app
        )
      );

      alert('Application withdrawn successfully. The employer will be notified.');

    } catch (error) {
      console.error('Error withdrawing application:', error);
      alert('Error withdrawing application. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    if (filter === 'pending-response') return app.status === APPLICATION_STATUSES.ACCEPTED && app.timeoutEnabled;
    return app.status === filter;
  });

  // Get counts for different statuses
  const getStatusCounts = () => {
    return {
      total: applications.length,
      pending: applications.filter(app => app.status === APPLICATION_STATUSES.PENDING).length,
      accepted: applications.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length,
      studentAccepted: applications.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length,
      studentDeclined: applications.filter(app => app.status === APPLICATION_STATUSES.STUDENT_DECLINED).length,
      rejected: applications.filter(app => app.status === APPLICATION_STATUSES.REJECTED).length,
      interview: applications.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length,
      withdrawn: applications.filter(app => app.status === APPLICATION_STATUSES.WITHDRAWN).length,
      awaitingResponse: applications.filter(app => 
        app.status === APPLICATION_STATUSES.ACCEPTED && app.timeoutEnabled
      ).length
    };
  };

  const statusCounts = getStatusCounts();

  const getStatusColor = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case APPLICATION_STATUSES.ACCEPTED: return 'bg-blue-100 text-blue-800 border-blue-200';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'bg-green-100 text-green-800 border-green-200';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'bg-orange-100 text-orange-800 border-orange-200';
      case APPLICATION_STATUSES.REJECTED: return 'bg-red-100 text-red-800 border-red-200';
      case APPLICATION_STATUSES.INTERVIEW: return 'bg-purple-100 text-purple-800 border-purple-200';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'bg-gray-100 text-gray-800 border-gray-200';
      case APPLICATION_STATUSES.WITHDRAWN: return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return <FaClock className="mr-1" />;
      case APPLICATION_STATUSES.ACCEPTED: return <FaBell className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return <FaCheck className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_DECLINED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.REJECTED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.INTERVIEW: return <FaCalendar className="mr-1" />;
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return <FaExclamationTriangle className="mr-1" />;
      case APPLICATION_STATUSES.WITHDRAWN: return <FaTimes className="mr-1" />;
      default: return <FaClock className="mr-1" />;
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'Under Review';
      case APPLICATION_STATUSES.ACCEPTED: return 'Offer Received';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'Offer Accepted';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'Offer Declined';
      case APPLICATION_STATUSES.REJECTED: return 'Not Selected';
      case APPLICATION_STATUSES.INTERVIEW: return 'Interview Scheduled';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'Offer Expired';
      case APPLICATION_STATUSES.WITHDRAWN: return 'Withdrawn';
      default: return status;
    }
  };

  // Application Card Component
  const ApplicationCard = ({ application }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-lg mb-2">{application.positionTitle}</h3>
          <p className="text-gray-600 mb-1">{application.companyName}</p>
          <p className="text-sm text-gray-500">
            Applied: {new Date(application.appliedAt).toLocaleDateString()}
          </p>
          {application.acceptedAt && (
            <p className="text-sm text-gray-500">
              Accepted: {new Date(application.acceptedAt.toDate()).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
          {getStatusIcon(application.status)}
          {getStatusDisplayName(application.status)}
        </div>
      </div>

      {/* Offer Response Component for Accepted Applications */}
      {application.status === APPLICATION_STATUSES.ACCEPTED && application.timeoutEnabled && (
        <div className="mb-4">
          <OfferResponseComponent 
            application={application}
            loading={responding}
            onResponse={(action, reason) => handleOfferResponse(action, application, reason)}
          />
        </div>
      )}

      {/* Additional Status Information */}
      {application.status === APPLICATION_STATUSES.STUDENT_ACCEPTED && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center text-green-800">
            <FaCheck className="mr-2 text-green-600" />
            <span className="font-semibold">Congratulations! 🎉</span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            You've accepted this internship offer. The employer will contact you with next steps.
          </p>
        </div>
      )}

      {application.status === APPLICATION_STATUSES.STUDENT_DECLINED && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex items-center text-orange-800">
            <FaTimes className="mr-2 text-orange-600" />
            <span className="font-semibold">Offer Declined</span>
          </div>
          <p className="text-orange-700 text-sm mt-1">
            You declined this internship offer.
          </p>
          {application.declineReason && (
            <p className="text-orange-600 text-xs mt-2">
              Reason: {application.declineReason}
            </p>
          )}
        </div>
      )}

      {application.status === APPLICATION_STATUSES.TIMEOUT_DECLINED && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center text-gray-800">
            <FaExclamationTriangle className="mr-2 text-gray-600" />
            <span className="font-semibold">Offer Expired</span>
          </div>
          <p className="text-gray-700 text-sm mt-1">
            This offer expired due to no response within the deadline.
          </p>
        </div>
      )}

      {application.status === APPLICATION_STATUSES.INTERVIEW && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex items-center text-purple-800">
            <FaCalendar className="mr-2 text-purple-600" />
            <span className="font-semibold">Interview Opportunity</span>
          </div>
          <p className="text-purple-700 text-sm mt-1">
            The employer is interested and may contact you for an interview.
          </p>
        </div>
      )}

      {application.status === APPLICATION_STATUSES.WITHDRAWN && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center text-gray-800">
            <FaTimes className="mr-2 text-gray-600" />
            <span className="font-semibold">Application Withdrawn</span>
          </div>
          <p className="text-gray-700 text-sm mt-1">
            You withdrew this application on {new Date(application.withdrawnAt || application.updatedAt).toLocaleDateString()}.
          </p>
        </div>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <button
          onClick={() => {
            setSelectedApplication(application);
            setShowApplicationModal(true);
          }}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          <FaEye className="mr-1" />
          View Details
        </button>

        <div className="flex items-center space-x-3">
          {/* Withdraw button for pending applications */}
          {application.status === APPLICATION_STATUSES.PENDING && (
            <button
              onClick={() => handleWithdrawApplication(application.id)}
              disabled={responding}
              className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
            >
              <FaTimes className="mr-1" />
              Withdraw
            </button>
          )}

          {/* Action buttons based on status */}
          {application.status === APPLICATION_STATUSES.ACCEPTED && application.timeoutInfo?.isUrgent && (
            <div className="flex items-center text-red-600 text-sm">
              <FaExclamationTriangle className="mr-1" />
              Urgent Response Needed
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Application Details Modal
  const ApplicationModal = () => {
    if (!selectedApplication) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Application Details
            </h2>
            <button
              onClick={() => setShowApplicationModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Position Information</h3>
              <p className="text-lg font-medium text-gray-800">{selectedApplication.positionTitle}</p>
              <p className="text-gray-600">{selectedApplication.companyName}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">Status</h4>
                <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedApplication.status)}`}>
                  {getStatusIcon(selectedApplication.status)}
                  {getStatusDisplayName(selectedApplication.status)}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">Applied Date</h4>
                <p className="text-gray-600">
                  {new Date(selectedApplication.appliedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {selectedApplication.coverLetter && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Cover Letter</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {selectedApplication.coverLetter}
                </p>
              </div>
            )}

            {selectedApplication.whyInterested && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Why Interested</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {selectedApplication.whyInterested}
                </p>
              </div>
            )}

            {selectedApplication.relevantExperience && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Relevant Experience</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {selectedApplication.relevantExperience}
                </p>
              </div>
            )}

            {selectedApplication.availability && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Availability</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {selectedApplication.availability}
                </p>
              </div>
            )}

            {/* Show offer response in modal if applicable */}
            {selectedApplication.status === APPLICATION_STATUSES.ACCEPTED && selectedApplication.timeoutEnabled && (
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-700 mb-4">Respond to Offer</h4>
                <OfferResponseComponent 
                  application={selectedApplication}
                  loading={responding}
                  onResponse={(action, reason) => {
                    handleOfferResponse(action, selectedApplication, reason);
                    setShowApplicationModal(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 pt-10 pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Applications</h1>
            <p className="text-gray-600">Track your internship applications and respond to offers</p>
          </div>
        </div>

        {/* Priority Offers Alert */}
        {statusCounts.awaitingResponse > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <FaBell className="text-blue-600 mr-3 text-lg" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">
                  You have {statusCounts.awaitingResponse} pending offer{statusCounts.awaitingResponse !== 1 ? 's' : ''}!
                </h3>
                <p className="text-blue-800 text-sm mt-1">
                  Please respond to your offers before they expire. Employers are waiting for your decision.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total</h3>
            <p className="text-2xl font-bold text-gray-900">{statusCounts.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Under Review</h3>
            <p className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Offers</h3>
            <p className="text-2xl font-bold text-blue-600">{statusCounts.awaitingResponse}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Accepted</h3>
            <p className="text-2xl font-bold text-green-600">{statusCounts.studentAccepted}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Declined</h3>
            <p className="text-2xl font-bold text-orange-600">{statusCounts.studentDeclined}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Interview</h3>
            <p className="text-2xl font-bold text-purple-600">{statusCounts.interview}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Withdrawn</h3>
            <p className="text-2xl font-bold text-gray-600">{statusCounts.withdrawn}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Not Selected</h3>
            <p className="text-2xl font-bold text-red-600">{statusCounts.rejected}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Applications ({statusCounts.total})
            </button>
            <button
              onClick={() => setFilter('pending-response')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending-response' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaBell className="inline mr-1" />
              Pending Response ({statusCounts.awaitingResponse})
            </button>
            <button
              onClick={() => setFilter(APPLICATION_STATUSES.STUDENT_ACCEPTED)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === APPLICATION_STATUSES.STUDENT_ACCEPTED 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Accepted ({statusCounts.studentAccepted})
            </button>
            <button
              onClick={() => setFilter(APPLICATION_STATUSES.PENDING)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === APPLICATION_STATUSES.PENDING 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Under Review ({statusCounts.pending})
            </button>
            <button
              onClick={() => setFilter(APPLICATION_STATUSES.WITHDRAWN)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === APPLICATION_STATUSES.WITHDRAWN 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Withdrawn ({statusCounts.withdrawn})
            </button>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
            <FaClock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'No applications yet' : 'No applications in this category'}
            </h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'Start exploring internship opportunities and submit your first application!'
                : 'Try adjusting your filter to see more applications.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredApplications.map((application) => (
              <ApplicationCard key={application.id} application={application} />
            ))}
          </div>
        )}

        {/* Application Details Modal */}
        {showApplicationModal && <ApplicationModal />}

        {/* Loading Overlay */}
        {responding && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center">
              <FaSpinner className="animate-spin mr-3 text-orange-500" />
              <span>Processing your response...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Applications;